import { isProd } from '@m/config/FeatureFlags';

import type { ContactInfoFields } from 'apps/customer/containers/Onboarding/contactInfoDisplay';

const SESSION_KEY = 'qa_contactFields';
const VALID_VALUES = ['email', 'nameAndEmail'] as const satisfies readonly ContactInfoFields[];

function isOverrideValue(value: string | null): value is ContactInfoFields {
  return (VALID_VALUES as readonly string[]).includes(value ?? '');
}

/**
 * Call once on app load to capture ?contactFields=<email|nameAndEmail> into sessionStorage, so QA
 * can force an arm of the web_onboarding_contact_fields experiment for the rest of the session.
 * Separate from ?expressOnboarding=, which picks the onboarding flow rather than the contact arm.
 */
export function captureQAContactFieldsOverride(search: string): void {
  if (isProd) return;
  const value = new URLSearchParams(search).get('contactFields');
  if (isOverrideValue(value)) {
    sessionStorage.setItem(SESSION_KEY, value);
  }
}

/**
 * Returns the current or persisted QA contact-fields override when not in production. Reads the
 * query string first: capture runs in an effect after the first render, so on a cold load straight
 * onto the contact step only the URL has the value yet.
 */
export function getQAContactFieldsOverride(
  search = window.location.search,
): ContactInfoFields | null {
  if (isProd) return null;
  const queryOverride = new URLSearchParams(search).get('contactFields');
  if (isOverrideValue(queryOverride)) return queryOverride;

  const stored = sessionStorage.getItem(SESSION_KEY);
  return isOverrideValue(stored) ? stored : null;
}
